/**
 * useWeatherOutlook — polls the two feeds behind WeatherOutlookPanel and keeps
 * each response WHOLE, meta included.
 *
 * Why not useFeed: useFeed unwraps `features` and returns T[], which drops
 * `meta.note`. That note is the entire point of this panel — it is what tells
 * the reader the rain column is a model forecast and not a gauge reading — so
 * losing it would quietly remove the honesty the panel exists to provide.
 * Same SWR-style localStorage roundtrip as useFloodPrevention, so the panel
 * paints from disk on a cold load instead of flashing a spinner.
 */

import { useEffect, useState } from "react";
import type { FloodForecastVerification, NormalizedFeed, WeatherSeries } from "@chula/shared";

interface Result {
  series: NormalizedFeed<WeatherSeries> | null;
  verification: NormalizedFeed<FloodForecastVerification> | null;
  /** The forecast series is still in flight. */
  isLoading: boolean;
  /** The FloodDash scorecard is still in flight — a separate question. */
  verificationLoading: boolean;
  error: string | null;
}

const POLL_MS = 30 * 60 * 1000;
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;
/**
 * A request that never answers is a question, not a fact. Without a ceiling
 * the panel stays in its loading state forever and the rail shows an empty
 * hole where the forecast should be. The local API answers in milliseconds;
 * twenty seconds means something is genuinely wrong, and the honest answer to
 * "we do not know" is to say so.
 */
const FETCH_TIMEOUT_MS = 20_000;

const SERIES_KEY = "chula:outlook:series";
const VERIFY_KEY = "chula:outlook:verification";

function readCache<T>(key: string): NormalizedFeed<T> | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { feed: NormalizedFeed<T>; savedAt: number };
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > MAX_CACHE_AGE_MS) return null;
    return parsed.feed ?? null;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, feed: NormalizedFeed<T>): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify({ feed, savedAt: Date.now() }));
  } catch {
    /* quota or private-mode — ignore */
  }
}

async function getFeed<T>(url: string, signal: AbortSignal): Promise<NormalizedFeed<T>> {
  // Own controller so a hung request can be given a deadline without
  // pretending the component unmounted.
  const ctrl = new AbortController();
  let timedOut = false;
  const onAbort = () => ctrl.abort();
  signal.addEventListener("abort", onAbort, { once: true });
  const timer = setTimeout(() => { timedOut = true; ctrl.abort(); }, FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: ctrl.signal, cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
    const json = (await res.json()) as NormalizedFeed<T>;
    if (!Array.isArray(json?.features) || !json?.meta) throw new Error("Invalid feed response");
    return json;
  } catch (err) {
    if (timedOut) throw new Error(`No answer after ${Math.round(FETCH_TIMEOUT_MS / 1000)}s`);
    throw err;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", onAbort);
  }
}

export function useWeatherOutlook(apiBase: string): Result {
  const [series, setSeries] = useState<NormalizedFeed<WeatherSeries> | null>(() => readCache(SERIES_KEY));
  const [verification, setVerification] = useState<NormalizedFeed<FloodForecastVerification> | null>(
    () => readCache(VERIFY_KEY),
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(
    () => readCache<WeatherSeries>(SERIES_KEY) == null,
  );
  const [verificationLoading, setVerificationLoading] = useState<boolean>(
    () => readCache<FloodForecastVerification>(VERIFY_KEY) == null,
  );

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();

    // Series and verification settle independently. The verification card is
    // a qualifier on the flood signal, not a prerequisite for the forecast,
    // so a FloodDash outage must never blank the weather outlook — and a
    // hanging FloodDash must never hold the forecast hostage either.
    const loadSeries = async () => {
      try {
        const value = await getFeed<WeatherSeries>(`${apiBase}/api/weather/series?_=${Date.now()}`, ctrl.signal);
        if (cancelled) return;
        setSeries(value);
        writeCache(SERIES_KEY, value);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        if ((err as Error)?.name !== "AbortError") setError((err as Error).message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    const loadVerification = async () => {
      try {
        const value = await getFeed<FloodForecastVerification>(`${apiBase}/api/flood/verification?_=${Date.now()}`, ctrl.signal);
        if (cancelled) return;
        setVerification(value);
        writeCache(VERIFY_KEY, value);
      } catch {
        // A rejected verification is deliberately silent: the card renders its
        // own "withheld, not guessed" state, and the adapter already returns a
        // well-formed unavailable feed for a down FloodDash.
      } finally {
        if (!cancelled) setVerificationLoading(false);
      }
    };

    void loadSeries();
    void loadVerification();
    const id = setInterval(() => { void loadSeries(); void loadVerification(); }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
      ctrl.abort();
    };
  }, [apiBase]);

  return { series, verification, isLoading, verificationLoading, error };
}